import Microgame from '../engine/Microgame.js';

export default class PeelGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Épluche !`, en: `Peel!`, es: `¡Pela!` },
            controls: { fr: `Glisse ton doigt vers le bas le long du fruit`, en: `Swipe down along the fruit`, es: `Desliza el dedo hacia abajo a lo largo de la fruta` },
            objective: { fr: `Retire toute la peau du fruit avent la fin`, en: `Remove all the skin from the fruit before time ends`, es: `Quita toda la piel de la fruta antes de que acabe el tiempo` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="M12 22s8-4 8-10A8 8 0 0 0 4 12c0 6 8 10 8 10z"/><path d="M12 2s2 2 2 4"/></svg>`,
            actions: [
                { type: 'wait', duration: 420 },
                { type: 'swipe', fromX: 52, fromY: 48, toX: 78, toY: 74, duration: 280 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.postits = [];
        this.activePostit = null;
        this.startX = 0;
        this.startY = 0;
        this.isPeeling = false;

        // Difficulty scaling
        const speedMult = engine.speedMultiplier || 1.0;

        // Number of post-its decreases as speed increases
        // x1: 35 post-its (hard to spot)
        // x4: 10 post-its (easier to spot, very little time)
        this.postItCount = Math.max(8, Math.floor(35 - (speedMult - 1) * 8));

        // Tighter duration, steepens heavily at high difficulty
        // x1: ~2500ms, x4: ~600ms
        const adjustedSpeed = 1 + (speedMult - 1) * (2.8 / 3);
        this.duration = 2500 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "PEEL TARGET!";

        // Container for post-its
        const wall = document.createElement('div');
        wall.style.position = 'absolute';
        wall.style.width = '100%';
        wall.style.height = '100%';
        wall.style.top = '0';
        wall.style.left = '0';
        this.container.appendChild(wall);

        // Generate Post-its
        // We ensure EXACTLY ONE primary colored post-it
        // Put the target deeper in the pile (between 25% and 60% depth), so it's always buried noticeably
        const minTargetIdx = Math.floor(this.postItCount * 0.25);
        const maxTargetIdx = Math.floor(this.postItCount * 0.60);
        const targetIndex = minTargetIdx + Math.floor(Math.random() * Math.max(1, maxTargetIdx - minTargetIdx));

        let targetLeft = 0;
        let targetTop = 0;
        let overlapCount = 0;

        for (let i = 0; i < this.postItCount; i++) {
            const isTarget = i === targetIndex;

            const postit = document.createElement('div');
            postit.classList.add('postit');
            postit.dataset.target = isTarget;

            let left = 15 + Math.random() * 60;
            let top = 15 + Math.random() * 60;
            const rot = Math.random() * 70 - 35; // -35 to +35 deg

            if (isTarget) {
                targetLeft = left;
                targetTop = top;
            } else if (i > targetIndex) {
                // If this postit is ABOVE the target, check for heavy overlap
                const dx = Math.abs(left - targetLeft);
                const dy = Math.abs(top - targetTop);
                // If it falls within a 20% radius of the target's center (significant overlap)
                if (dx < 20 && dy < 20) {
                    overlapCount++;
                    // Only allow exactly ONE false post-it to directly cover the center of the target
                    if (overlapCount > 1) {
                        // Push subsequent cover-ups far away to guarantee an exposed edge
                        left += (left >= targetLeft ? 25 : -25);
                        top += (top >= targetTop ? 25 : -25);
                    }
                }
            }

            postit.dataset.rot = rot;

            const size = isTarget ? 110 : 90; // Target is bigger, impossible to perfectly cover with smaller ones
            postit.style.cssText = `
                position: absolute;
                width: ${size}px;
                height: ${size}px;
                background: ${isTarget ? this.primaryColor : this.secondaryColor};
                left: ${left}%;
                top: ${top}%;
                transform: rotate(${rot}deg);
                box-shadow: 2px 4px 10px rgba(0,0,0,0.3);
                border-radius: 2px;
                cursor: grab;
                touch-action: none;
                z-index: ${i}; /* Normal stacking instead of 100 ensures target is partially buried */
            `;

            // Optional: A little folded corner for aesthetics
            const fold = document.createElement('div');
            fold.style.cssText = `
                position: absolute;
                bottom: 0; right: 0;
                width: 20px; height: 20px;
                background: rgba(0,0,0,0.15);
                border-top-left-radius: 20px;
                pointer-events: none;
            `;
            postit.appendChild(fold);

            // Add an icon or marking on the target to make it even more obvious if colors are close
            if (isTarget) {
                const icon = document.createElement('div');
                icon.textContent = "★";
                icon.style.cssText = `
                    position: absolute;
                    top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 2rem;
                    color: rgba(255,255,255,0.7);
                    pointer-events: none;
                `;
                postit.appendChild(icon);
            }

            wall.appendChild(postit);
            this.postits.push(postit);
        }

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
        // Handle pointer leaving the screen during a drag
        this.engine.input.element.addEventListener('pointerleave', this.onUp);
    }

    onDown(e) {
        if (!this.active || this.won) return;

        // Find all elements under the pointer (handles overlapping)
        let elements = document.elementsFromPoint(e.clientX, e.clientY);

        let clickedPostit = null;
        let foundTarget = false;

        // First check if the target post-it is anywhere under the finger (forgive top layers)
        for (let el of elements) {
            if (el.classList.contains('postit')) {
                if (el.dataset.target === 'true') {
                    clickedPostit = el;
                    foundTarget = true;
                    break;
                } else if (!clickedPostit) {
                    // Save the topmost wrong post-it just in case the target isn't here
                    clickedPostit = el;
                }
            }
        }

        if (clickedPostit) {
            this.activePostit = clickedPostit;
            this.startX = e.clientX;
            this.startY = e.clientY;
            this.isPeeling = true;

            // Visual feedback
            this.activePostit.style.transition = 'none';
            this.activePostit.style.zIndex = '999'; // Bring to front while peeling
        }
    }

    onMove(e) {
        if (!this.active || !this.isPeeling || !this.activePostit) return;

        const dx = e.clientX - this.startX;
        const dy = e.clientY - this.startY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        const origRot = parseFloat(this.activePostit.dataset.rot);

        // 3D-like peel effect: lift and rotate slightly
        this.activePostit.style.transform = `translate(${dx}px, ${dy}px) rotate(${origRot + dx * 0.1}deg) scale(1.05)`;
        this.activePostit.style.boxShadow = `10px 15px 25px rgba(0,0,0,0.4)`;

        // If dragged far enough -> Peel it completely
        if (dist > 60) {
            const isTarget = this.activePostit.dataset.target === 'true';

            // Throw away animation
            this.activePostit.style.transition = 'all 0.4s ease-out';
            this.activePostit.style.transform = `translate(${dx * 3}px, ${dy * 3}px) rotate(${origRot + 90}deg) scale(0)`;
            this.activePostit.style.opacity = '0';

            this.isPeeling = false;

            if (isTarget) {
                this.hideTimer = true;
                this.win();
            } else {
                // Picked the wrong one
                this.lose();
            }

            this.activePostit = null;
        }
    }

    onUp() {
        if (!this.active) return;

        if (this.isPeeling && this.activePostit && !this.won) {
            // Snap back if released too early
            const origRot = this.activePostit.dataset.rot;
            const isTarget = this.activePostit.dataset.target === 'true';

            this.activePostit.style.transition = 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)';
            this.activePostit.style.transform = `rotate(${origRot}deg) scale(1)`;
            this.activePostit.style.boxShadow = `2px 4px 10px rgba(0,0,0,0.3)`;
            this.activePostit.style.zIndex = isTarget ? '100' : '0';
        }

        this.isPeeling = false;
        this.activePostit = null;
    }

    update(dt) {
        // Nothing needed here, interaction is event-based
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
        this.engine.input.element.removeEventListener('pointerleave', this.onUp);
    }
}
